/* global globalThis */
import React, { useMemo, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./CustomMultiSelect.module.css";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
export default function CustomMultiSelect({
  options = [],
  value = [],
  onChange,
  prefixLabel = "Action Suggested",
  placeholder = "Select",
  showSelectAll = true,
  maxLabelChars = 24,
  width = "260px" // NEW: control overall width (e.g., "50%", "400px")
}) {
  const ALL = "Select All";
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // NEW: local search query state
  const [query, setQuery] = useState("");

  const isAllSelected = options.length > 0 && value.length === options.length;

  const displayText = useMemo(() => {
    if (isAllSelected) return "All";
    if (!value?.length) return placeholder;
    const joined = value.join(", ");
    return joined.length > maxLabelChars ? joined.slice(0, maxLabelChars) + "..." : joined;
  }, [isAllSelected, value, placeholder, maxLabelChars]);

  // NEW: filter options by query (case-insensitive)
  const filteredOptions = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter(opt => String(opt).toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (item) => {
    if (item === ALL) {
      if (isAllSelected) onChange?.([]);
      else onChange?.([...options]); // Select all across full list (not only filtered)
      return;
    }
    const exists = value.includes(item);
    const next = exists ? value.filter((v) => v !== item) : [...value, item];
    onChange?.(next);
  };

  const handleToggle = () => setOpen((s) => !s);

 useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    if (typeof globalThis === "undefined") return undefined;
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, []);

  // NEW: close on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!open) return;
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  // NEW: clear search when dropdown closes
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <div
      className={styles.wrapper}
      ref={boxRef}
      style={{ "--cms-width": width }}   // NEW: pass width to CSS
    >
      <button
        type="button"
        className={`${styles.control} ${open ? styles.controlOpen : ""} ${isAllSelected ? styles.controlTight : ""}`}
        onClick={handleToggle}
      >
        <span className={styles.prefix}>{prefixLabel}:</span>
        <span
          className={`${styles.value} ${isAllSelected ? styles.valueAll : ""} ${!value?.length && !isAllSelected ? styles.placeholder : ""}`}
        >
          {displayText}
        </span>
        <span className={`${styles.caret} ${open ? styles.caretOpen : ""}`} aria-hidden>
        <ArrowDropDownIcon sx={{ fontSize: 18, color:"rgba(0, 0, 0, 0.87)" }} />
        </span>
      </button>

      {open && (
        <div className={styles.menu}>
          {/* NEW: Search input */}
          <div className={styles.searchBox}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          {showSelectAll && (
            <button
              type="button"
              className={styles.item}
              onClick={() => handleSelect(ALL)}
              title="All"
            >
              <input type="checkbox" readOnly checked={isAllSelected} className={styles.checkbox} />
              <span className={styles.itemLabel}>All</span>
            </button>
          )}

          {/* NEW: show message if no matches */}
          {filteredOptions.length === 0 && (
            <div className={styles.item} aria-disabled title="No results">
              <span className={styles.itemLabel}>No results</span>
            </div>
          )}

          {filteredOptions.map((opt) => {
            const checked = value.includes(opt);
            return (
              <button
                type="button"
                key={opt}
                className={`${styles.item} ${checked ? styles.itemActive : ""}`}
                onClick={() => handleSelect(opt)}
                title={opt}
              >
                <input type="checkbox" readOnly checked={checked} className={styles.checkbox} />
                <span className={styles.itemLabel}>{opt}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

CustomMultiSelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.array,
  onChange: PropTypes.func,
  prefixLabel: PropTypes.string,
  placeholder: PropTypes.string,
  showSelectAll: PropTypes.bool,
  maxLabelChars: PropTypes.number,
  width: PropTypes.string,
};